import { useContext, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Container,
  Spinner,
  Table,
} from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import { FaArrowLeft } from "react-icons/fa";
import jsPDF from "jspdf";
import "jspdf-autotable";

import "../styles/GRNDetails.css";

const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";

export default function RawMaterialsGRNDetails() {
  const { grn_id } = useParams();
  const navigate = useNavigate();
  const { userData } = useContext(AuthContext);

  const [grnDetails, setGRNDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchGRNDetails = async () => {
      try {
        const response = await fetch(
          `${BASE_URL}/api/admin/grn/raw-materials-grn-details/${grn_id}?company_code=${userData.company_code}`,
        );
        const data = await response.json();

        if (data.success) {
          setGRNDetails(data);
        } else {
          setError(data.message || "Failed to fetch GRN details");
        }
      } catch (err) {
        setError("Error fetching GRN details");
      } finally {
        setLoading(false);
      }
    };

    fetchGRNDetails();
  }, [grn_id, userData.company_code]);

  // generate PDF
  const handleDownload = (grnId) => {
    if (!grnDetails) return;

    const doc = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    });

    doc.setFontSize(16);
    doc.text(`${grnId}`, 14, 18);

    let y = 25; // top margin
    let tableCount = 0;
    let maxYInRow = y; // Track the tallest table in the current row

    grnDetails.items.forEach((item, idx) => {
      const tableBody = [
        ["Material ID", item.raw_material_id || ""],
        ["Material Name", item.material_name || ""],
        ["Material Size", item.material_size || ""],
        ["Ordered Qty", item.ordered_qty],
        ["Received Qty", item.received_qty],
        ["Qty Type", item.qty_type || ""],
        ["Lot Number", item.lot_no || ""],
        ["Location", item.location || ""],
      ];

      // Calculate left margin for left/right positioning
      const leftMargin = tableCount % 2 === 0 ? 14 : 108;

      doc.autoTable({
        startY: y,
        margin: { left: leftMargin },
        head: [],
        body: tableBody,
        theme: "grid",
        styles: {
          fontSize: 8,
          cellPadding: 1.5,
          textColor: [0, 0, 0], // Black text
          lineColor: [0, 0, 0], // Black borders
          lineWidth: 0.3, // Thicker borders for visibility
        },
        columnStyles: {
          0: {
            fontStyle: "bold",
            halign: "left",
            cellWidth: 25,
            textColor: [0, 0, 0], // Black text
          },
          1: {
            halign: "left",
            cellWidth: 63,
            fillColor: [255, 255, 255], // White background for values
            textColor: [0, 0, 0], // Black text
          },
        },
        tableWidth: 88, // fixed width for side by side
      });

      // Track the maximum Y position in the current row
      maxYInRow = Math.max(maxYInRow, doc.lastAutoTable.finalY);

      // After each table, decide placement
      tableCount++;
      if (tableCount % 2 === 0) {
        // Move to next row after 2 tables
        y = maxYInRow + 5;
        maxYInRow = y;
      }

      // Page break if near bottom
      if (maxYInRow > 250 && idx < grnDetails.items.length - 1) {
        doc.addPage();
        y = 20;
        tableCount = 0;
        maxYInRow = y;
      }
    });

    doc.save(`GRN_${grnId}.pdf`);
  };

  if (loading) {
    return (
      <Container className="text-center py-5">
        <Spinner animation="border" />
        <p>Loading GRN details...</p>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="py-5">
        <Alert variant="danger">{error}</Alert>
        <Button
          variant="secondary"
          onClick={() => navigate(-1)}
          style={{ padding: "10px" }}
        >
          <FaArrowLeft /> Go Back
        </Button>
      </Container>
    );
  }

  if (!grnDetails) {
    return (
      <Container className="py-5">
        <Alert variant="warning">GRN not found</Alert>
        <Button
          variant="secondary"
          onClick={() => navigate(-1)}
          style={{ padding: "10px" }}
        >
          <FaArrowLeft /> Go Back
        </Button>
      </Container>
    );
  }

  return (
    <Container fluid className="grn-details-section">
      <div className="d-flex justify-content-between align-items-center mb-4 grn-details-header">
        <h1>GRN Details: {grnDetails.header.grn_id}</h1>
        <Button
          variant="secondary"
          onClick={() => navigate(-1)}
          style={{ padding: "10px" }}
        >
          <FaArrowLeft />
          Back to GRN List
        </Button>
      </div>

      <Card className="mb-4">
        <Card.Header className="d-flex justify-content-between align-items-center mb-4">
          <h5>GRN Information</h5>
          {/* <FaDownload */}
          <Button
            variant="primary"
            onClick={() => {
              handleDownload(grnDetails.header.grn_id);
            }}
            title="Download GRN Item Details"
            style={{ padding: "10px" }}
          >
            Download GRN Item Details
          </Button>
          {/* /> */}
        </Card.Header>
        <Card.Body>
          <div className="row">
            <div className="col-md-6">
              <p>
                <strong>PO Number:</strong> {grnDetails.header.po_number}
              </p>
              <p>
                <strong>Supplier:</strong>{" "}
                {grnDetails.header.supplier_name ||
                  grnDetails.header.supplier_id}
              </p>
              <p>
                <strong>Received Date:</strong>{" "}
                {new Date(grnDetails.header.received_date).toLocaleString()}
              </p>
            </div>
            <div className="col-md-6">
              <p>
                <strong>Status: </strong>
                {grnDetails.header.status}
              </p>
              <p>
                <strong>Batch Number:</strong>{" "}
                {grnDetails.header.batch_number || "N/A"}
              </p>
              <p>
                <strong>Invoice Number:</strong>{" "}
                {grnDetails.header.invoice_number || "N/A"}
              </p>
            </div>
          </div>
          {grnDetails.header.reference && (
            <p>
              <strong>Reference:</strong> {grnDetails.header.reference}
            </p>
          )}
        </Card.Body>
      </Card>

      <Card>
        <Card.Header>
          <h5>Received Items ({grnDetails.items.length})</h5>
        </Card.Header>
        <Card.Body>
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th>Material ID</th>
                <th>Material Name</th>
                <th>Material Size</th>
                <th>Ordered Qty</th>
                <th>Received Qty</th>
                <th>Qty Type</th>
                <th>Lot Number</th>
                <th>Location</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              {grnDetails.items.map((item) => (
                <tr key={item.raw_material_id}>
                  <td>{item.raw_material_id || "N/A"}</td>
                  <td>{item.material_name || "N/A"}</td>
                  <td>{item.material_size || "N/A"}</td>
                  <td className="text-end">{item.ordered_qty}</td>
                  <td className="text-end">{item.received_qty}</td>
                  <td>{item.qty_type || "N/A"}</td>
                  <td>{item.lot_no || "N/A"}</td>
                  <td>{item.location || "N/A"}</td>
                  <td>{item.notes || "-"}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>
    </Container>
  );
}
